import {
  ActionIcon,
  Group,
  NumberInput,
  Select,
  Stack,
  Tooltip,
} from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import { useCallback, useMemo } from "react";
import type { PanelBillingMode } from "../../../services/admin/panels.service";
import type { PanelComponentFormValue, PanelSelectOption } from "./panelForm";

interface PanelComponentEditorProps {
  index: number;
  value: PanelComponentFormValue;
  billingMode: PanelBillingMode;
  stainOptions: PanelSelectOption[];
  cptOptions: PanelSelectOption[];
  errors: Partial<Record<keyof PanelComponentFormValue, string>>;
  canRemove: boolean;
  materialLocked?: boolean;
  onChange: (index: number, value: PanelComponentFormValue) => void;
  onRemove: (index: number) => void;
}

const POLICY_OPTIONS = [
  { value: "standard", label: "Standard CPT allocation" },
  { value: "none", label: "No individual CPT" },
  { value: "override", label: "Use a specific CPT" },
];

export function PanelComponentEditor({
  index,
  value,
  billingMode,
  stainOptions,
  cptOptions,
  errors,
  canRemove,
  materialLocked = false,
  onChange,
  onRemove,
}: PanelComponentEditorProps) {
  const policyDisabled = useMemo(
    () => billingMode !== "group_plus_individual",
    [billingMode]
  );
  const policyDescription = useMemo(() => {
    if (billingMode === "group_only") return "Suppressed by group-only billing";
    if (billingMode === "individual_only") return "Uses normal stain billing";
    return "Choose how this component contributes an individual CPT";
  }, [billingMode]);

  const handleStainChange = useCallback(
    (stainId: string | null) => {
      onChange(index, { ...value, stainId: stainId ?? "" });
    },
    [index, onChange, value]
  );
  const handleQuantityChange = useCallback(
    (quantity: string | number) => {
      onChange(index, { ...value, quantity: Number(quantity) });
    },
    [index, onChange, value]
  );
  const handlePolicyChange = useCallback(
    (billingPolicy: string | null) => {
      const policy = billingPolicy as PanelComponentFormValue["billingPolicy"];
      onChange(index, {
        ...value,
        billingPolicy: policy,
        overrideCptId: policy === "override" ? value.overrideCptId : "",
      });
    },
    [index, onChange, value]
  );
  const handleOverrideChange = useCallback(
    (overrideCptId: string | null) => {
      onChange(index, { ...value, overrideCptId: overrideCptId ?? "" });
    },
    [index, onChange, value]
  );
  const handleRemove = useCallback(() => onRemove(index), [index, onRemove]);

  return (
    <Stack gap="sm" p="md" bg="var(--mantine-color-default-hover)" bdrs="md">
      <Group align="flex-start" wrap="nowrap">
        <Select
          label={`Component ${index + 1}`}
          placeholder="Select a stain"
          data={stainOptions}
          value={value.stainId}
          onChange={handleStainChange}
          error={errors.stainId}
          searchable
          clearable
          withAsterisk
          disabled={materialLocked}
          style={{ flex: 1 }}
        />
        <NumberInput
          label="Quantity per block"
          value={value.quantity}
          onChange={handleQuantityChange}
          error={errors.quantity}
          min={1}
          max={100}
          step={1}
          allowDecimal={false}
          withAsterisk
          w={170}
          disabled={materialLocked}
        />
        <Tooltip
          label={
            materialLocked ? "Protected workflow material" : "Remove component"
          }
        >
          <ActionIcon
            mt={28}
            size={44}
            variant="subtle"
            color="red"
            aria-label={`Remove component ${index + 1}`}
            disabled={!canRemove || materialLocked}
            onClick={handleRemove}
          >
            <IconTrash size={18} />
          </ActionIcon>
        </Tooltip>
      </Group>
      <Group align="flex-start" grow>
        <Select
          label="Individual CPT policy"
          description={policyDescription}
          data={POLICY_OPTIONS}
          value={value.billingPolicy}
          onChange={handlePolicyChange}
          disabled={policyDisabled}
          allowDeselect={false}
        />
        {value.billingPolicy === "override" && (
          <Select
            label="Override CPT"
            placeholder="Select a CPT code"
            data={cptOptions}
            value={value.overrideCptId}
            onChange={handleOverrideChange}
            error={errors.overrideCptId}
            searchable
            clearable
            withAsterisk
          />
        )}
      </Group>
    </Stack>
  );
}
